import React, { Children, cloneElement, isValidElement, useState } from 'react';
import classnames from 'classnames';
import { useTranslate } from 'ra-core';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { makeStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import AccountCircle from '@material-ui/icons/AccountCircle';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { Link, useLocation } from 'react-router-dom';
import { CurrentUserId } from './dataProvider/userContexts';
import { UserOrganizationSwitcher, useUserContext } from './userContexts';
/**
 * An hook to get the path of the related list of resources
 * from the current location.
 *
 * @example <caption>Simple usage</caption>
 *
 * const MyComponent = () => {
 *     const location = useLocation();
 *     console.log(location.pathname); // Shows "/apis/uuid-of-api/show"
 *
 *     const resourceListLocation = useResourceListLocation();
 *     console.log(resourceListLocation) // Show "/apis"
 * };
 *
 */
export const useResourceListLocation = () => {
    const location = useLocation();
    const RESOURCE_LIST_MATCH = /\/[^/]*/;
    const match = location.pathname.match(RESOURCE_LIST_MATCH);
    return match[0];
};
/**
 * The ApiHub UserMenu used in the ApiHub AppBar.
 *
 * @param {*} props UserMenu properties
 *
 * @example <caption>Simple usage</caption>
 * <ApiHubUserMenu />
 *
 * const MyAppBar = props => <ApiHubAppBar userMenu={ApiHubUserMenu} {...props} />
 *
 */
export const ApiHubUserMenu = props => {
    var _a, _b;
    const translate = useTranslate();
    const classes = useStyles(props);
    const [anchorEl, setAnchorEl] = useState(null);
    const redirectTo = useResourceListLocation();
    const [userContext, _handleChangeUserProfile, // eslint-disable-line no-unused-vars
    handleChangeUserActiveOrg,] = useUserContext(redirectTo);
    const { children, label, icon, logout } = props;
    if (!logout && !children) {
        return null;
    }
    const userName = userContext
        ? translate('apihub.menu.user_details.full_name', {
            last_name: ((_a = userContext === null || userContext === void 0 ? void 0 : userContext.userDetails) === null || _a === void 0 ? void 0 : _a.lastName) ? userContext.userDetails.lastName
                : '',
            first_name: ((_b = userContext === null || userContext === void 0 ? void 0 : userContext.userDetails) === null || _b === void 0 ? void 0 : _b.firstName) ? userContext.userDetails.firstName
                : '',
        })
        : '';
    const open = Boolean(anchorEl);
    const handleMenu = event => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleChangeUserContext = value => {
        handleChangeUserActiveOrg(value);
        handleClose();
    };
    return (React.createElement("div", null,
        React.createElement(Tooltip, { title: label && translate(label, { _: label }) },
            React.createElement(Button, { "aria-label": label && translate(label, { _: label }), "aria-owns": open ? 'menu-appbar' : null, "aria-haspopup": true, color: "inherit", onClick: handleMenu, startIcon: icon, endIcon: React.createElement(ArrowDropDownIcon, null), key: userName }, userName)),
        React.createElement(Menu, { id: "menu-appbar", anchorEl: anchorEl, anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
            }, transformOrigin: {
                vertical: 'top',
                horizontal: 'right',
            }, open: open, onClose: handleClose },
            React.createElement(MenuItem, { component: Link, to: `/userProfiles/${CurrentUserId}/show`, onClick: handleClose, className: classes.menuItem },
                React.createElement(ListItemIcon, { className: classes.icon },
                    React.createElement(AccountCircle, null)),
                translate('resources.userProfiles.actions.edit_profile')),
            React.createElement(Divider, { className: classes.divider }),
            React.createElement(UserOrganizationSwitcher, { userContext: userContext, onChangeUserContext: handleChangeUserContext }),
            Children.map(children, menuItem => isValidElement(menuItem)
                ? cloneElement(menuItem, {
                    className: classes.menuItem,
                    onClick: handleClose,
                })
                : null),
            cloneElement(logout, {
                className: classnames(classes.menuItem, classes.menuItemLogout),
            }))));
};
ApiHubUserMenu.defaultProps = {
    label: 'ra.auth.user_menu',
    icon: React.createElement(AccountCircle, null),
};
const useStyles = makeStyles(theme => ({
    menuItem: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(),
    },
    menuItemLogout: {
        marginBottom: '0px',
    },
    divider: {
        marginBottom: theme.spacing(),
    },
    icon: {
        minWidth: theme.spacing(5),
    },
}), {
    name: 'ApiHubUserMenu',
});
//# sourceMappingURL=ApiHubUserMenu.js.map